import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, trim } from 'lodash';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Typography,
} from '@material-ui/core';

import { useIsCurrentUserAdmin } from 'src/store';
import { useFetchReviewsAccounts } from 'src/services';
import isURL from 'validator/lib/isURL';
import { REVIEWS_CHANNEL, getChannelLabel } from 'src/data/channel';
import { CustomInput } from 'src/components/CustomInput';
import ChannelIcon from 'src/components/ChannelIcon';
import { EarthIcon } from 'src/assets/icons/Earth';
import { TooltipPerformanceNotAdmin } from 'src/components/TooltipPerformanceNotAdmin';
import { useViewState } from './useViewState';
import { useStyles } from './useStyles';

const getPlaceholder = (channel) => {
  switch (channel) {
    case REVIEWS_CHANNEL.amazon:
      return 'https://www.amazon.com/All-Metal-Dacoity-Multimedia-Anti-ghosting-Waterproof/dp/B09TZWLFLY/';
    case REVIEWS_CHANNEL.g2:
      return 'https://www.g2.com/products/anyword/reviews';
    case REVIEWS_CHANNEL.trustpilot:
    default:
      return 'https://www.trustpilot.com/review/anyword.com';
  }
};

export const ConnectReviewAccountsPopup = ({ open, channel, onSubmit, onDisconnect, onClose }) => {
  const styles = useStyles();

  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const isAdmin = useIsCurrentUserAdmin();
  const { accounts, selectedIds, loadingAccounts } = useViewState(channel);

  useFetchReviewsAccounts();
  const connectedAccount = accounts.find((a) => a.id === selectedIds[0]);

  useEffect(() => {
    setUrl(connectedAccount?.urls[0]);
  }, [connectedAccount]);

  const handleConfirm = useCallback(() => {
    if (isEmpty(trim(url || ''), { ignore_whitespace: true })) {
      setError('Please enter a URL');
      return;
    }
    if (!isURL(url)) {
      setError('Please enter a valid URL');
      return;
    }

    setError('');
    onSubmit({ url, channel });
  }, [channel, url, onSubmit]);

  const label = getChannelLabel(channel);

  return (
    <Dialog
      open={open}
      classes={{ paper: styles.root }}
      onClose={onClose}
      maxWidth="md"
      data-testid="ConnectReviewAccountsPopup"
    >
      <DialogTitle className={styles.dialogTitle}>
        <Typography className={styles.title}>
          <ChannelIcon
            className={styles.channelIcon}
            channel={channel}
          />
          Connect {label}
        </Typography>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <Typography className={styles.description}>
          Enter the {label} reviews page you want to connect with Anyword
        </Typography>

        {loadingAccounts ? (
          <CircularProgress className="block mx-auto" />
        ) : (
          <>
            <Typography className={styles.label}>Enter website address</Typography>
            <CustomInput
              className={styles.url}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={getPlaceholder(channel)}
              startAdornment={
                <InputAdornment position="start">
                  <EarthIcon className={styles.earthIcon} />
                </InputAdornment>
              }
              error={!!error}
              autoFocus
            />
            {error && <Typography className={styles.error}>{error}</Typography>}
          </>
        )}
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        {connectedAccount && (
          <Button
            className={styles.buttonDisconnect}
            onClick={() => onDisconnect(channel)}
            variant="text"
          >
            Disconnect
          </Button>
        )}
        <Button
          className={styles.buttonCancel}
          onClick={onClose}
          variant="text"
          color="primary"
          aria-label="Cancel"
          data-testid="ConnectReviewAccountsPopup__CancelButton"
        >
          Cancel
        </Button>
        <TooltipPerformanceNotAdmin isAdmin={isAdmin}>
          <Button
            className={styles.buttonConfirm}
            onClick={handleConfirm}
            disabled={!isAdmin || !url}
            variant="contained"
            color="primary"
            aria-label="Confirm"
            data-testid="ConnectReviewAccountsPopup__CTAButton"
          >
            Apply
          </Button>
        </TooltipPerformanceNotAdmin>
      </DialogActions>
    </Dialog>
  );
};

ConnectReviewAccountsPopup.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
ConnectReviewAccountsPopup.defaultProps = {
  className: null,
  open: false,
};
